import { ApiMethod } from "../models/api-types.type";
import { useAuthContext } from "./use-auth-context.hook";

/**
 * Trade: professionals near the vendor (public company registries) and the
 * professionals already among their leads — feat/room-to-revenue, workstream F1.
 * Endpoints under services/pms/trade (pms-api-v2 src/pms/pms-trade).
 */
const BASE = '/services/pms/trade';

export const TRADE_STATUSES = ['new', 'contacted', 'meeting', 'customer', 'ignored'] as const;
export type TradeStatus = (typeof TRADE_STATUSES)[number];
export type TradeIntroLang = 'fr' | 'en' | 'es' | 'ar';

export interface TradeOrigin {
    lat: number;
    lng: number;
    label: string;
    source?: 'manual' | 'store' | 'website' | 'address';
    storeId?: string;
}

/** An address suggestion for the origin field (GET places: the French address register). */
export interface TradePlace {
    label: string;
    city: string;
    postcode: string;
    context: string;
    type: 'municipality' | 'street' | 'housenumber' | 'locality';
    lat: number;
    lng: number;
}

export interface TradePlacesResult {
    items: TradePlace[];
    /** The address search did not answer; typing an address and saving still works. */
    unavailable?: boolean;
}

export interface TradeRefreshSummary {
    at: string;
    calls: number;
    rows: number;
    found: number;
    created: number;
    updated: number;
    truncated: boolean;
    error?: string;
    trigger: 'manual' | 'schedule';
    available?: number;
}

export interface TradeSettings {
    origin: TradeOrigin | null;
    radiusKm: number;
    activities: string[];
    country: string;
    refresh: 'weekly' | 'off';
    includeSoleTraders: boolean;
    tradeOffer: string;
    lastRefreshAt: string | null;
    lastRefresh: TradeRefreshSummary | null;
    lastLeadCheckAt: string | null;
}

export interface TradeActivityOption {
    code: string;
    group: 'fitters' | 'specifiers' | 'builders';
    labelFr: string;
    labelEn: string;
    defaultOn: boolean;
}

export interface TradeSettingsView {
    settings: TradeSettings;
    stores: Array<{ id: string; name: string; city?: string; lat: number; lng: number }>;
    activities: TradeActivityOption[];
    source: { id: string; country: string; label: string };
    countries: string[];
    offerPlaceholder: string;
}

export interface TradeIntro {
    subject: string;
    email: string;
    phone: string;
    lang: TradeIntroLang;
    model: string;
    generatedAt: string;
    hasPlaceholder: boolean;
}

export interface TradeProspect {
    _id: string;
    source: string;
    siren: string;
    siret: string;
    name: string;
    tradeName?: string | null;
    activity: { code: string; label: string };
    address?: string | null;
    postcode?: string | null;
    city?: string | null;
    lat?: number;
    lng?: number;
    distanceKm?: number;
    headcountBand?: string;
    createdOn?: string | null;
    establishments?: number | null;
    nearbyEstablishments?: number | null;
    category?: string | null;
    soleTrader: boolean;
    rge?: boolean;
    leaders: Array<{ name: string; role?: string }>;
    revenue?: { year: string; amount: number } | null;
    status: TradeStatus;
    statusAt?: string;
    score: number;
    reasons: string[];
    notes?: string;
    intro?: TradeIntro;
    firstSeenAt?: string;
    lastSeenAt?: string;
    publicUrl: string;
}

export interface TradeProspectPage {
    items: TradeProspect[];
    total: number;
    page: number;
    limit: number;
    counts: {
        total: number;
        byStatus: Record<TradeStatus, number>;
        byActivity: Array<{ code: string; label: string; count: number }>;
        withIntro: number;
    };
    refreshing: boolean;
}

export interface TradeProspectQuery {
    status?: 'open' | 'all' | TradeStatus;
    activity?: string;
    q?: string;
    maxKm?: number;
    sort?: 'score' | 'distance' | 'name' | 'createdOn' | 'size' | 'status';
    order?: 'asc' | 'desc';
    page?: number;
    limit?: number;
}

export interface TradeMapPoint {
    _id: string;
    name: string;
    lat: number;
    lng: number;
    score: number;
    status: TradeStatus;
    activity: { code: string };
}

export interface TradeLead {
    _id: string;
    email: string;
    name?: string;
    firstSeenAt?: string;
    lastSeenAt?: string;
    eventCount?: number;
    products?: number;
    roomImageUrl?: string;
    audienceInfo?: {
        source: 'auto' | 'manual';
        reasons: string[];
        points?: number;
        confidence?: number;
        company?: { siren: string; name: string; activityCode?: string } | null;
        at: string;
    };
}

export interface TradeLeadsView {
    items: TradeLead[];
    counts: { trade: number; consumer: number; unclassified: number };
    lastCheckAt: string | null;
}

export interface TradeLeadsCheck {
    checked: number;
    trade: number;
    consumer: number;
    manual: number;
    changed: number;
    lookups: number;
}

const query = (params: Record<string, unknown>) => {
    const entries = Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '');
    return entries.length ? `?${entries.map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`).join('&')}` : '';
};

export const useTradeApi = () => {
    const { sendAuthGuardedRequest } = useAuthContext();
    const send = <T,>(method: ApiMethod, path: string, body?: unknown, init?: RequestInit) =>
        sendAuthGuardedRequest(method, path, body, init) as Promise<T>;

    return {
        getSettings: () => send<TradeSettingsView>(ApiMethod.GET, `${BASE}/settings`),
        updateSettings: (input: Partial<{
            origin: { lat: number; lng: number; label?: string };
            address: string;
            storeId: string;
            radiusKm: number;
            activities: string[];
            refresh: 'weekly' | 'off';
            includeSoleTraders: boolean;
            tradeOffer: string;
        }>) => send<TradeSettingsView>(ApiMethod.PUT, `${BASE}/settings`, input),

        /** Towns, postcodes and streets matching what was typed (3 characters or more). */
        searchPlaces: (q: string, init?: { signal?: AbortSignal }) =>
            send<TradePlacesResult>(ApiMethod.GET, `${BASE}/places${query({ q, limit: 6 })}`, undefined, init),

        refresh: () => send<TradeRefreshSummary>(ApiMethod.POST, `${BASE}/prospects/refresh`),
        list: (q: TradeProspectQuery) => send<TradeProspectPage>(ApiMethod.GET, `${BASE}/prospects${query({ ...q })}`),
        mapPoints: (q: TradeProspectQuery) => send<TradeMapPoint[]>(ApiMethod.GET, `${BASE}/prospects/map${query({ ...q, page: undefined, limit: undefined })}`),
        get: (id: string) => send<TradeProspect>(ApiMethod.GET, `${BASE}/prospects/${id}`),
        update: (id: string, input: { status?: TradeStatus; notes?: string; intro?: Partial<Pick<TradeIntro, 'subject' | 'email' | 'phone'>> }) =>
            send<TradeProspect>(ApiMethod.PATCH, `${BASE}/prospects/${id}`, input),
        writeIntro: (id: string, lang: TradeIntroLang = 'fr') => send<TradeProspect>(ApiMethod.POST, `${BASE}/prospects/${id}/intro`, { lang }),
        exportCsv: (q: TradeProspectQuery) =>
            sendAuthGuardedRequest(
                ApiMethod.GET,
                `${BASE}/prospects/export${query({ ...q, page: undefined, limit: undefined })}`,
                undefined,
                { responseType: 'arraybuffer' } as any,
            ) as Promise<ArrayBuffer>,

        getLeads: () => send<TradeLeadsView>(ApiMethod.GET, `${BASE}/leads`),
        checkLeads: () => send<TradeLeadsCheck>(ApiMethod.POST, `${BASE}/leads/classify`),
        setLeadAudience: (leadId: string, audience: 'trade' | 'consumer' | 'auto') =>
            send<{ leadId: string; audience: string }>(ApiMethod.PATCH, `${BASE}/leads/${leadId}`, { audience }),
    };
};
